/**
 * The targets Contact Detail's "Update status?" step offers for a guest on an event (#1013, VR-M1).
 *
 * The step used to list the pinned workflow's raw stage keys. The guest's column is derived from facts now, so a
 * target is either a fixed journey milestone (sent as `journey_target`, which the server maps to the facts that
 * milestone implies) or a follow-up stage of the pinned workflow (still sent as `outreach_stage`). Attendance
 * targets are offered only once the event has started: the server refuses them before (`attendance_not_open`).
 */

/** The fixed milestones, in pipeline order; `attendance` ones need a started event. */
export const JOURNEY_TARGETS = [
    { key: 'to_invite', label: 'To invite' },
    { key: 'invited', label: 'Invited' },
    { key: 'going', label: 'Going' },
    { key: 'attended', label: 'Attended', attendance: true },
    { key: 'no_show', label: 'No-show', attendance: true },
];

export const CLOSE_TARGET = 'closed';

/** The follow-up stages every workflow starts with (`event_journey.FOLLOW_UP_DEFAULTS`). */
const DEFAULT_FOLLOW_UP_STAGES = [
    { key: 'following_up', label: 'Following up' },
    { key: 'meeting_booked', label: 'Meeting booked' },
    { key: 'converted', label: 'Converted' },
];

/** The reasons a person can pick when closing a guest by hand (P2); `rescheduled` is written by Reschedule guest. */
export const CLOSE_REASONS = [
    { key: 'not_interested', label: 'Not interested' },
    { key: 'no_reply', label: 'No reply' },
    { key: 'never_invited', label: 'Never invited' },
    { key: 'wrong_person', label: 'Wrong person' },
    { key: 'declined', label: 'Declined' },
    { key: 'cancelled', label: 'Cancelled' },
];

const MILESTONE_KEYS = [...JOURNEY_TARGETS.map((target) => target.key), CLOSE_TARGET];
export const isMilestoneTarget = (key) => MILESTONE_KEYS.includes(key);

/** True once the event has begun: Start was pressed, or a pre-Start event's date has arrived. The server decides on save. */
export function eventHasStarted(event) {
    if (!event) return false;
    if (event.started_at || event.ended_at) return true;
    if (event.phase) return event.phase !== 'before';
    const day = String(event.date || event.start_date || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return day <= today;
}

/**
 * The options for the stage select: milestones, the pinned workflow's follow-up stages, then Closed. A stage of an
 * older snapshot's Before-the-event group is not a column any more and is left out, as is a stage that repeats a milestone.
 */
export function statusTargetOptions({ stages = [], started = false } = {}) {
    // An event that has no pinned workflow yet (nothing has been written to it) lists no stages at all: the guests of
    // such an event still move to the default follow-up stages, the ones the board shows.
    const known = (stages || []).length > 0 ? stages : DEFAULT_FOLLOW_UP_STAGES;
    const followUps = known
        .filter((stage) => (stage.group || stage.stage_group) !== 'before_event' && !isMilestoneTarget(stage.key || stage.stage_key))
        .map((stage) => ({ key: stage.key || stage.stage_key, label: stage.label || stage.key || stage.stage_key, followUp: true }));
    return [
        ...JOURNEY_TARGETS.filter((target) => started || !target.attendance),
        ...followUps,
        { key: CLOSE_TARGET, label: 'Closed' },
    ];
}

/** The PATCH fields for the chosen target: a milestone is a `journey_target`, a follow-up stage an `outreach_stage`. */
export function statusTargetFields(target, closeReason) {
    if (!target) return {};
    if (target === CLOSE_TARGET) return { journey_target: CLOSE_TARGET, close_reason: closeReason || CLOSE_REASONS[0].key };
    if (isMilestoneTarget(target)) return { journey_target: target };
    return { outreach_stage: target };
}
